import {keyframes} from '@emotion/react'
import {Transition} from 'framer-motion'
import {SxType} from '@baseTypes/sx/sxTypes'

export interface ISingleVoyageThemeTokens {
	accent: string
	accentHover: string
	accentSoft: string
	accentText: string
	bg: string
	surface: string
	surfaceMuted: string
	line: string
	lineStrong: string
	text: string
	text2: string
	shadowSm: string
	shadowMd: string
}

export const SV_LIGHT: ISingleVoyageThemeTokens = {
	accent: '#4574D4',
	accentHover: '#3A65BF',
	accentSoft: 'rgba(69, 116, 212, 0.12)',
	accentText: '#fff',
	bg: '#F4F6F8',
	surface: '#fff',
	surfaceMuted: '#F1F4F6',
	line: '#E3E6EA',
	lineStrong: '#D3D8DE',
	text: '#2A3439',
	text2: '#66717A',
	shadowSm: '0 1px 2px rgba(30, 42, 50, 0.05)',
	shadowMd: '0 4px 12px rgba(30, 42, 50, 0.08)',
}

export const SV_DARK: ISingleVoyageThemeTokens = {
	accent: '#5B8AE0',
	accentHover: '#7AA1EA',
	accentSoft: 'rgba(91, 138, 224, 0.18)',
	accentText: '#fff',
	bg: '#121417',
	surface: '#1C1F24',
	surfaceMuted: '#23272E',
	line: 'rgba(255, 255, 255, 0.10)',
	lineStrong: 'rgba(255, 255, 255, 0.18)',
	text: '#E6E9ED',
	text2: '#9AA3AD',
	shadowSm: '0 1px 2px rgba(0, 0, 0, 0.30)',
	shadowMd: '0 4px 12px rgba(0, 0, 0, 0.40)',
}

export const SV_RADIUS_SM = '8px'
export const SV_RADIUS_MD = '10px'
export const SV_RADIUS_LG = '12px'

export const SV_EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)'
export const SV_TRANSITION = `background-color .18s ease, border-color .18s ease, color .18s ease, box-shadow .2s ease, transform .2s ${SV_EASE}`

export const SV_SPRING: Transition = {type: 'spring', stiffness: 420, damping: 34, mass: 0.8}

export const svFadeUp = keyframes`
	from { opacity: 0; transform: translateY(8px); }
	to   { opacity: 1; transform: none; }
`

const pulseRingByColor = new Map<string, ReturnType<typeof keyframes>>()

const svPulseRing = (ringColor: string) => {
	let ring = pulseRingByColor.get(ringColor)
	if (!ring) {
		ring = keyframes`
			0%   { box-shadow: 0 0 0 0 ${ringColor}; }
			70%  { box-shadow: 0 0 0 6px transparent; }
			100% { box-shadow: 0 0 0 0 transparent; }
		`
		pulseRingByColor.set(ringColor, ring)
	}
	return ring
}

const SV_REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)'

export const svEnter = (index = 0): SxType => ({
	animation: `${svFadeUp} .38s ${SV_EASE} both`,
	animationDelay: `${Math.min(index, 12) * 40}ms`,
	[SV_REDUCED_MOTION]: {animation: 'none'},
})

export const svPressable: SxType = {
	'&:hover': {transform: 'translateY(-1px)'},
	'&:active': {transform: 'translateY(0) scale(0.98)'},
	[SV_REDUCED_MOTION]: {
		'&:hover, &:active': {transform: 'none'},
	},
}

export const svLiveDot = (dotColor: string): SxType => ({
	animation: `${svPulseRing(`${dotColor}66`)} 1.8s ease-out infinite`,
	[SV_REDUCED_MOTION]: {animation: 'none'},
})
